import React, { useState, useEffect } from 'react';
import apiClient, { getApiUrl } from '../../utils/apiClient';
import {
  Calculator, Download, Users, Calendar, DollarSign,
  FileText, Settings, Check, AlertCircle, Printer,
  Clock, TrendingUp, Filter, ChevronDown, ChevronUp, Home,
  Edit, Trash2, Plus
} from 'lucide-react';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import { ja } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

import PageContainer from '../layout/PageContainer';
import AttendanceEditModal from '../attendance/AttendanceEditModal';

interface PayrollData {
  staff_id: number;
  display_name: string;
  work_days: number;
  work_hours: number;
  overtime_hours: number;
  base_pay: number;
  overtime_pay: number;
  transport_allowance: number;
  meal_allowance: number;
  other_allowance: number;
  gross_pay: number;
  income_tax: number;
  health_insurance: number;
  pension: number;
  employment_insurance: number;
  total_deductions: number;
  net_pay: number;
  status: string;
}

interface StaffSettings {
  staff_id: number;
  display_name: string;
  pay_type: 'hourly' | 'daily' | 'monthly';
  base_rate: number;
  transport_allowance: number;
  meal_allowance: number;
  other_allowance: number;
  tax_exempt: boolean;
  insurance_enrolled: boolean;
  is_owner?: boolean;
}

interface AttendanceRecord {
  id: number;
  staff_id: number;
  staff_name: string;
  date: string;
  clock_in: string;
  clock_out: string | null;
  break_duration: number;
  work_hours: number;
  overtime_hours: number;
  wage_rules_applied: WageRuleApplication[];
  base_pay: number;
  overtime_pay: number;
  total_pay: number;
  // シフト情報
  shift_start?: string;
  shift_end?: string;
  shift_difference?: {
    has_shift: boolean;
    start_diff_minutes?: number;
    end_diff_minutes?: number;
    is_early_arrival?: boolean;
    is_late_arrival?: boolean;
    is_early_leave?: boolean;
    is_overtime?: boolean;
    is_absent?: boolean;
    is_unscheduled?: boolean;
    work_date?: string;
  break_start?: string | null;
  break_end?: string | null;
  notes?: string;
};
}

interface WageRuleApplication {
  rule_name: string;
  rule_type: string;
  amount: number;
  icon?: string;
  color?: string;
}

interface DailyAttendance {
  date: string;
  records: AttendanceRecord[];
  dailyTotal: number;
}

const PayrollManagementEnhanced: React.FC = () => {
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [payrollData, setPayrollData] = useState<PayrollData[]>([]);
  const [staffSettings, setStaffSettings] = useState<StaffSettings[]>([]);
  const [attendanceRecords, setAttendanceRecords] = useState<DailyAttendance[]>([]);
  const [activeTab, setActiveTab] = useState<'attendance' | 'calculate' | 'payslips' | 'settings' | 'export'>('attendance');

  // 勤怠編集用state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<any | null>(null);
  const [modalMode, setModalMode] = useState<'edit' | 'create'>('edit');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffSettings | null>(null);
  const [kyuryoohPreview, setKyuryoohPreview] = useState<any>(null);
  const [showKyuryoohPreview, setShowKyuryoohPreview] = useState(false);

  // 勤怠記録フィルター
  const [selectedStaffIds, setSelectedStaffIds] = useState<number[]>([]);
  const [staffList, setStaffList] = useState<{id: number, name: string}[]>([]);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchStaffList();
  }, []);

  useEffect(() => {
    if (activeTab === 'attendance') {
      fetchAttendanceRecords();
    } else if (activeTab === 'payslips') {
      fetchPayrollData();
    } else if (activeTab === 'settings') {
      fetchStaffSettings();
    }
  }, [activeTab, selectedMonth, selectedStaffIds]);

  const fetchStaffList = async () => {
    try {
      const response = await apiClient.get('/staff');
      const staffData = response.data.data || [];
      setStaffList(staffData.map((s: any) => ({ id: s.id, name: s.displayName })));
      // デフォルトで全員選択
      setSelectedStaffIds(staffData.map((s: any) => s.id));
    } catch (error) {
      console.error('スタッフリスト取得エラー:', error);
    }
  };

  const fetchAttendanceRecords = async () => {
    const [year, month] = selectedMonth.split('-');
    const startDate = format(startOfMonth(new Date(parseInt(year), parseInt(month) - 1)), 'yyyy-MM-dd');
    const endDate = format(endOfMonth(new Date(parseInt(year), parseInt(month) - 1)), 'yyyy-MM-dd');

    setLoading(true);
    try {
      // 勤怠記録とシフトの比較データを取得
      const attendanceResponse = await apiClient.get('/attendance-enhanced/records-with-shift', {
        params: {
          start_date: startDate,
          end_date: endDate,
          staff_ids: selectedStaffIds.join(',')
        }
      });

      // 時給ルール情報を取得
      const wageRulesResponse = await apiClient.get('/wage-rules');
      const wageRules = wageRulesResponse.data || [];

      // スタッフ設定を取得
      const settingsPromises = selectedStaffIds.map(async (staffId) => {
        const res = await apiClient.get(`/payroll/settings/${staffId}`);
        return { staffId, settings: res.data };
      });
      const staffSettingsData = await Promise.all(settingsPromises);
      const settingsMap = new Map(staffSettingsData.map(s => [s.staffId, s.settings]));

      // 日付ごとにグループ化して計算
      const recordsByDate = new Map<string, AttendanceRecord[]>();

      (attendanceResponse.data || []).forEach((record: any) => {
        const date = format(parseISO(record.clock_in), 'yyyy-MM-dd');
        const staffSetting = settingsMap.get(record.staff_id);

        // 基本給計算
        let basePay = 0;
        let overtimePay = 0;
        const appliedRules: WageRuleApplication[] = [];

        if (staffSetting) {
          const workHours = record.work_hours || 0;
          const overtimeHours = record.overtime_hours || 0;
          const baseRate = staffSetting.base_rate || 1000;

          // 基本時給
          basePay = Math.floor(workHours * baseRate);

          // 残業手当（25%増し）
          if (overtimeHours > 0) {
            overtimePay = Math.floor(overtimeHours * baseRate * 1.25);
            appliedRules.push({
              rule_name: '残業手当',
              rule_type: 'overtime',
              amount: overtimePay,
              icon: '⏰',
              color: '#EF4444'
            });
          }

          // 時給ルールの適用チェック
          const clockInTime = new Date(record.clock_in);
          const dayOfWeek = clockInTime.getDay();
          const hour = clockInTime.getHours();

          // 早朝手当（6時前開始）
          if (hour < 6) {
            const earlyBonus = Math.floor(workHours * 200);
            basePay += earlyBonus;
            appliedRules.push({
              rule_name: '早朝手当',
              rule_type: 'early_morning',
              amount: earlyBonus,
              icon: '🌅',
              color: '#F59E0B'
            });
          }

          // 深夜手当（22時以降）
          if (record.clock_out) {
            const clockOutTime = new Date(record.clock_out);
            if (clockOutTime.getHours() >= 22) {
              const nightBonus = Math.floor(workHours * 250);
              basePay += nightBonus;
              appliedRules.push({
                rule_name: '深夜手当',
                rule_type: 'late_night',
                amount: nightBonus,
                icon: '🌙',
                color: '#6366F1'
              });
            }
          }

          // 週末手当（土日）
          if (dayOfWeek === 0 || dayOfWeek === 6) {
            const weekendBonus = Math.floor(workHours * 150);
            basePay += weekendBonus;
            appliedRules.push({
              rule_name: '週末手当',
              rule_type: 'weekend',
              amount: weekendBonus,
              icon: '🎉',
              color: '#10B981'
            });
          }
        }

        const attendanceRecord: AttendanceRecord = {
          id: record.id,
          staff_id: record.staff_id,
          staff_name: record.staff_name || staffList.find(s => s.staff_id || s.id === record.staff_id)?.name || '',
          date,
          clock_in: record.clock_in,
          clock_out: record.clock_out,
          break_duration: record.break_duration || 0,
          work_hours: record.work_hours || 0,
          overtime_hours: record.overtime_hours || 0,
          wage_rules_applied: appliedRules,
          base_pay: basePay,
          overtime_pay: overtimePay,
          total_pay: basePay + overtimePay,
          shift_start: record.shift_start,
          shift_end: record.shift_end,
          shift_difference: record.shift_difference
        };

        if (!recordsByDate.has(date)) {
          recordsByDate.set(date, []);
        }
        recordsByDate.get(date)!.push(attendanceRecord);
      });

      // DailyAttendance形式に変換
      const dailyAttendances: DailyAttendance[] = [];
      const dates = eachDayOfInterval({
        start: startOfMonth(new Date(parseInt(year), parseInt(month) - 1)),
        end: endOfMonth(new Date(parseInt(year), parseInt(month) - 1))
      });

      dates.forEach(date => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const records = recordsByDate.get(dateStr) || [];
        const dailyTotal = records.reduce((sum, r) => sum + (r.total_pay || 0), 0);

        if (records.length > 0) {
          dailyAttendances.push({
            date: dateStr,
            records,
            dailyTotal
          });
        }
      });

      setAttendanceRecords(dailyAttendances);
    } catch (error) {
      console.error('勤怠記録取得エラー:', error);
      setMessage({ type: 'error', text: '勤怠記録の取得に失敗しました' });
    } finally {
      setLoading(false);
    }
  };

  const fetchPayrollData = async () => {
    const [year, month] = selectedMonth.split('-');
    try {
      const response = await apiClient.get(`/payroll/payslip/${year}/${month}`);
      setPayrollData(response.data);
    } catch (error) {
      console.error('給与データ取得エラー:', error);
      setPayrollData([]);
    }
  };

  const fetchStaffSettings = async () => {
    try {
      const response = await apiClient.get('/staff');
      const staffList = response.data.data || [];

      const settingsPromises = staffList.map(async (staff: any) => {
        const settingsRes = await apiClient.get(`/payroll/settings/${staff.id}`);
        return {
          staff_id: staff.id,
          display_name: staff.displayName,
          ...settingsRes.data
        };
      });

      const settings = await Promise.all(settingsPromises);
      setStaffSettings(settings);
    } catch (error) {
      console.error('スタッフ設定取得エラー:', error);
    }
  };

  const handleCalculatePayroll = async () => {
    const [year, month] = selectedMonth.split('-');
    console.log("💰 給与計算開始 - 対象月:", selectedMonth);
    setLoading(true);
    setMessage(null);

    try {
      const response = await apiClient.post(`/payroll/calculate/${year}/${month}`);
      const apiUrl = `${getApiUrl()}/payroll/calculate/${year}/${month}`;
      console.log("📡 給与計算APIリクエスト送信:", apiUrl);
      setMessage({ type: 'success', text: response.data.message });
      console.log("✅ 給与計算API成功:", response.data);
      
      // 給与データを再取得して給与明細タブに切り替え
      fetchPayrollData();
      setActiveTab('payslips');
    } catch (error: any) {
      console.error("給与計算エラー:", error);
      setMessage({ type: 'error', text: error.response?.data?.error || '給与計算に失敗しました' });
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    const [year, month] = selectedMonth.split('-');

    try {
      const response = await apiClient.get(
        `/payroll/export/aoiro/${year}/${month}`,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `給与仕訳_${year}年${month}月.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setMessage({ type: 'success', text: 'CSVファイルをダウンロードしました' });
    } catch (error) {
      setMessage({ type: 'error', text: 'CSV出力に失敗しました' });
    }
  };

  // 給料王用CSVエクスポート
  const handleKyuryoohExport = async () => {
    const [year, month] = selectedMonth.split('-');

    try {
      const response = await apiClient.get(
        `/payroll/export/kyuryooh/${year}/${month}`,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `kintai_${year}${month.padStart(2, '0')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setMessage({ type: 'success', text: '給料王用CSVファイルをダウンロードしました' });
    } catch (error) {
      setMessage({ type: 'error', text: '給料王用CSV出力に失敗しました' });
    }
  };

  // 給料王プレビューデータ取得
  const fetchKyuryoohPreview = async () => {
    const [year, month] = selectedMonth.split('-');
    setLoading(true);

    try {
      const response = await apiClient.get(
        `/payroll/export/kyuryooh/preview/${year}/${month}`
      );
      setKyuryoohPreview(response.data);
      setShowKyuryoohPreview(true);
    } catch (error) {
      console.error('プレビュー取得エラー:', error);
      setMessage({ type: 'error', text: '勤怠データの取得に失敗しました' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (settings: StaffSettings) => {
    try {
      await apiClient.post(`/payroll/settings/${settings.staff_id}`, settings);
      setMessage({ type: 'success', text: '設定を保存しました' });
      setShowSettingsModal(false);
      fetchStaffSettings();
    } catch (error) {
      setMessage({ type: 'error', text: '設定の保存に失敗しました' });
    }
  };

  const formatCurrency = (amount: number | undefined | null) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return '¥0';
    }
    return `¥${amount.toLocaleString()}`;
  };

  const formatTime = (datetime: string | null) => {
    if (!datetime) return '-';
    return format(parseISO(datetime), 'HH:mm');
  };

  const toggleDateExpansion = (date: string) => {
    const newExpanded = new Set(expandedDates);
    if (newExpanded.has(date)) {
      newExpanded.delete(date);
    } else {
      newExpanded.add(date);
    }
    setExpandedDates(newExpanded);
  };

  const toggleStaffFilter = (staffId: number) => {
    if (selectedStaffIds.includes(staffId)) {
      setSelectedStaffIds(selectedStaffIds.filter(id => id !== staffId));
    } else {
      setSelectedStaffIds([...selectedStaffIds, staffId]);
    }
  };

  // 勤怠編集ハンドラー
  const handleEditRecord = (record: AttendanceRecord, date: string) => {
    setEditingRecord({
      id: record.id,
      staff_id: record.staff_id,
      staff_name: record.staff_name,
      work_date: date,
      clock_in: record.clock_in,
      clock_out: record.clock_out,
      break_start: record.break_start,
      break_end: record.break_end,
      notes: record.notes || ''
    });
    setModalMode('edit');
    setIsEditModalOpen(true);
  };

  const handleCreateRecord = () => {
    setEditingRecord(null);
    setModalMode('create');
    setIsEditModalOpen(true);
  };

  const handleDeleteRecord = async (recordId: number) => {
    if (!window.confirm('この勤怠記録を削除しますか？')) {
      return;
    }

    try {
      await apiClient.delete(`/attendance/record/${recordId}`);
      alert('勤怠記録を削除しました');
      fetchAttendanceRecords(); // データ再取得
    } catch (error) {
      console.error('削除エラー:', error);
      alert('削除に失敗しました');
    }
  };

  const handleSaveRecord = async (data: any) => {
    try {
      if (modalMode === 'edit') {
        await apiClient.put(`/attendance/record/${data.id}`, data);
        alert('勤怠記録を更新しました');
      } else {
        await apiClient.post('/attendance/record/manual', data);
        alert('勤怠記録を追加しました');
      }
      fetchAttendanceRecords(); // データ再取得
    } catch (error) {
      console.error('保存エラー:', error);
      throw error;
    }
  };

  // スタッフリストの準備（新規追加時に使用）
  const editModalStaffList = staffSettings.map(s => ({
    id: s.staff_id,
    name: s.display_name
  }));

  return (
    <PageContainer>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <Calculator className="h-8 w-8 text-green-600" />
            給与・勤怠管理
          </h1>
          <button
            onClick={() => navigate('/home')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Home className="w-5 h-5" />
            ホーム
          </button>
        </div>

        {/* タブ */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="flex border-b overflow-x-auto">
            <button
              onClick={() => setActiveTab('attendance')}
              className={`px-6 py-3 font-medium whitespace-nowrap ${
                activeTab === 'attendance'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Clock className="inline h-4 w-4 mr-2" />
              勤怠記録
            </button>
            <button
              onClick={() => setActiveTab('calculate')}
              className={`px-6 py-3 font-medium whitespace-nowrap ${
                activeTab === 'calculate'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Calculator className="inline h-4 w-4 mr-2" />
              給与計算
            </button>
            <button
              onClick={() => setActiveTab('payslips')}
              className={`px-6 py-3 font-medium whitespace-nowrap ${
                activeTab === 'payslips'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <FileText className="inline h-4 w-4 mr-2" />
              給与明細
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-6 py-3 font-medium whitespace-nowrap ${
                activeTab === 'settings'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Settings className="inline h-4 w-4 mr-2" />
              給与設定
            </button>
            <button
              onClick={() => setActiveTab('export')}
              className={`px-6 py-3 font-medium whitespace-nowrap ${
                activeTab === 'export'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Download className="inline h-4 w-4 mr-2" />
              会計連携
            </button>
          </div>
        </div>

        {/* 月選択とフィルター */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <Calendar className="h-5 w-5 text-gray-600" />
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {activeTab === 'attendance' && (
              <div className="flex items-center gap-4">
                <Filter className="h-5 w-5 text-gray-600" />
                <div className="flex gap-2 flex-wrap">
                  {staffList.map(staff => (
                    <label key={staff.id} className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={selectedStaffIds.includes(staff.id)}
                        onChange={() => toggleStaffFilter(staff.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm">{staff.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'calculate' && (
              <button
                onClick={handleCalculatePayroll}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Calculator className="h-4 w-4" />
                {loading ? '計算中...' : '給与計算実行'}
              </button>
            )}
          </div>
        </div>

        {/* メッセージ */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message.type === 'success' ? (
              <Check className="h-5 w-5" />
            ) : (
              <AlertCircle className="h-5 w-5" />
            )}
            <span className="font-medium">{message.text}</span>
          </div>
        )}

        {/* コンテンツ */}
        <div className="bg-white rounded-lg shadow">
          {/* 勤怠記録タブ */}
          {activeTab === 'attendance' && (
            <div className="p-6">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">読み込み中...</p>
                </div>
              ) : attendanceRecords.length > 0 ? (
                <div className="space-y-4">
                  {attendanceRecords.map((daily) => (
                    <div key={daily.date} className="border rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleDateExpansion(daily.date)}
                        className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <span className="font-semibold text-lg">
                            {format(parseISO(daily.date), 'M月d日(E)', { locale: ja })}
                          </span>
                          <span className="text-sm text-gray-600">
                            {daily.records.length}名出勤
                          </span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-bold text-green-600">
                            {formatCurrency(daily.dailyTotal)}
                          </span>
                          {expandedDates.has(daily.date) ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      </button>

                      {expandedDates.has(daily.date) && (
                        <div className="border-t">
                          <table className="w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">スタッフ</th>
                                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">シフト</th>
                                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">実績</th>
                                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">差異</th>
                                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">休憩</th>
                                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">労働時間</th>
                                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">残業</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">適用ルール</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">基本給</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">手当</th>
                                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">合計</th>
                                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">操作</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {daily.records.map((record) => (
                                <tr key={record.id} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 font-medium">{record.staff_name}</td>
                                  {/* シフト時間 */}
                                  <td className="px-4 py-3 text-center text-xs">
                                    {record.shift_start && record.shift_end ? (
                                      <div className="text-gray-600">
                                        {record.shift_start.slice(0, 5)}～{record.shift_end.slice(0, 5)}
                                      </div>
                                    ) : record.shift_difference?.is_unscheduled ? (
                                      <span className="text-orange-600 font-medium">シフト外</span>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </td>
                                  {/* 実績時間 */}
                                  <td className="px-4 py-3 text-center text-xs">
                                    <div className={record.shift_difference?.is_absent ? 'text-red-600 font-bold' : ''}>
                                      {record.shift_difference?.is_absent ? (
                                        '欠勤'
                                      ) : (
                                        <>
                                          {formatTime(record.clock_in)}～{formatTime(record.clock_out)}
                                        </>
                                      )}
                                    </div>
                                  </td>
                                  {/* 差異表示 */}
                                  <td className="px-4 py-3 text-center text-xs">
                                    {record.shift_difference && record.shift_difference.has_shift && (
                                      <div className="space-y-1">
                                        {record.shift_difference.is_late_arrival && (
                                          <span className="inline-block px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded text-xs">
                                            遅刻 {record.shift_difference.start_diff_minutes}分
                                          </span>
                                        )}
                                        {record.shift_difference.is_early_arrival && (
                                          <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">
                                            早出 {Math.abs(record.shift_difference.start_diff_minutes || 0)}分
                                          </span>
                                        )}
                                        {record.shift_difference.is_early_leave && (
                                          <span className="inline-block px-2 py-0.5 bg-orange-100 text-orange-800 rounded text-xs">
                                            早退 {Math.abs(record.shift_difference.end_diff_minutes || 0)}分
                                          </span>
                                        )}
                                        {record.shift_difference.is_overtime && (
                                          <span className="inline-block px-2 py-0.5 bg-purple-100 text-purple-800 rounded text-xs">
                                            残業 {record.shift_difference.end_diff_minutes}分
                                          </span>
                                        )}
                                        {!record.shift_difference.is_late_arrival &&
                                         !record.shift_difference.is_early_arrival &&
                                         !record.shift_difference.is_early_leave &&
                                         !record.shift_difference.is_overtime && (
                                          <span className="text-green-600">✓</span>
                                        )}
                                      </div>
                                    )}
                                    {record.shift_difference?.is_unscheduled && (
                                      <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-800 rounded text-xs">
                                        臨時出勤
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-center">{record.break_duration}分</td>
                                  <td className="px-4 py-3 text-center font-medium">{record.work_hours.toFixed(1)}h</td>
                                  <td className="px-4 py-3 text-center">
                                    {record.overtime_hours > 0 && (
                                      <span className="text-red-600 font-medium">
                                        {record.overtime_hours.toFixed(1)}h
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex gap-1 flex-wrap">
                                      {record.wage_rules_applied.map((rule, idx) => (
                                        <span
                                          key={idx}
                                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                                          style={{ backgroundColor: rule.color + '20', color: rule.color }}
                                        >
                                          <span>{rule.icon}</span>
                                          <span>{rule.rule_name}</span>
                                          <span className="font-bold">+{formatCurrency(rule.amount)}</span>
                                        </span>
                                      ))}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-right">{formatCurrency(record.base_pay || 0)}</td>
                                  <td className="px-4 py-3 text-right">
                                    {(record.overtime_pay || 0) > 0 && (
                                      <span className="text-orange-600">
                                        {formatCurrency(record.overtime_pay || 0)}
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-right font-bold text-green-600">
                                    {formatCurrency(record.total_pay || 0)}
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <div className="flex items-center justify-center gap-2">
                                      <button
                                        onClick={() => handleEditRecord(record, daily.date)}
                                        className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                        title="編集"
                                      >
                                        <Edit className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteRecord(record.id)}
                                        className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                        title="削除"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* 月間サマリー */}
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">月間サマリー</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-blue-700">総労働時間</p>
                        <p className="text-2xl font-bold text-blue-900">
                          {attendanceRecords.reduce((sum, d) =>
                            sum + d.records.reduce((s, r) => s + r.work_hours, 0), 0
                          ).toFixed(1)}h
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-blue-700">総残業時間</p>
                        <p className="text-2xl font-bold text-red-600">
                          {attendanceRecords.reduce((sum, d) =>
                            sum + d.records.reduce((s, r) => s + r.overtime_hours, 0), 0
                          ).toFixed(1)}h
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-blue-700">延べ出勤人数</p>
                        <p className="text-2xl font-bold text-blue-900">
                          {attendanceRecords.reduce((sum, d) => sum + d.records.length, 0)}名
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-blue-700">月間給与総額</p>
                        <p className="text-2xl font-bold text-green-600">
                          {formatCurrency(
                            attendanceRecords.reduce((sum, d) => sum + d.dailyTotal, 0)
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  選択された期間の勤怠記録がありません
                </div>
              )}
            </div>
          )}

          {/* 給与計算タブ */}
          {activeTab === 'calculate' && (
            <div className="p-6">
              <div className="max-w-3xl mx-auto mb-6 border-2 border-amber-300 bg-amber-50 rounded-lg p-4">
                <p className="text-sm font-semibold text-amber-900 mb-3">
                  ※ここは勤怠集計の参考表示です。最終の給与確定（控除・税・社保・年末調整）は『給料王』で行ってください。
                </p>
                <button
                  onClick={handleKyuryoohExport}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  給料王CSV（推奨）
                </button>
              </div>
              <div className="text-center py-12">
                <Calculator className="h-24 w-24 mx-auto text-gray-400 mb-4" />
                <h2 className="text-2xl font-semibold text-gray-700 mb-2">
                  給与計算を実行
                </h2>
                <p className="text-gray-600 mb-6">
                  {selectedMonth.split('-')[0]}年{parseInt(selectedMonth.split('-')[1])}月の勤怠データから給与を自動計算します
                </p>
                <div className="bg-blue-50 rounded-lg p-4 max-w-md mx-auto">
                  <p className="text-sm text-blue-800">
                    計算内容：基本給、残業手当、各種手当、源泉徴収、社会保険料
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* 給与明細タブ */}
          {activeTab === 'payslips' && (
            <div className="p-6">
              {payrollData.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">スタッフ</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">勤務日数</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">労働時間</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">基本給</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">手当</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">総支給</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">控除</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">手取り</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">操作</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {payrollData.map((payroll, index) => (
                        <tr key={`${payroll.staff_id}-${index}`}>
                          <td className="px-4 py-4 font-medium">{payroll.display_name}</td>
                          <td className="px-4 py-4 text-right">{payroll.work_days}日</td>
                          <td className="px-4 py-4 text-right">{payroll.work_hours.toFixed(1)}時間</td>
                          <td className="px-4 py-4 text-right">{formatCurrency(payroll.base_pay)}</td>
                          <td className="px-4 py-4 text-right">
                            {formatCurrency(
                              payroll.overtime_pay +
                              payroll.transport_allowance +
                              payroll.meal_allowance +
                              payroll.other_allowance
                            )}
                          </td>
                          <td className="px-4 py-4 text-right font-semibold">{formatCurrency(payroll.gross_pay)}</td>
                          <td className="px-4 py-4 text-right text-red-600">{formatCurrency(payroll.total_deductions)}</td>
                          <td className="px-4 py-4 text-right font-bold text-green-600">{formatCurrency(payroll.net_pay)}</td>
                          <td className="px-4 py-4 text-center">
                            <button className="text-blue-600 hover:text-blue-700">
                              <Printer className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-100">
                      <tr>
                        <td className="px-4 py-3 font-bold">合計</td>
                        <td className="px-4 py-3 text-right font-bold">
                          {payrollData.reduce((sum, p) => sum + p.work_days, 0)}日
                        </td>
                        <td className="px-4 py-3 text-right font-bold">
                          {payrollData.reduce((sum, p) => sum + p.work_hours, 0).toFixed(1)}時間
                        </td>
                        <td className="px-4 py-3 text-right font-bold">
                          {formatCurrency(payrollData.reduce((sum, p) => sum + p.base_pay, 0))}
                        </td>
                        <td className="px-4 py-3"></td>
                        <td className="px-4 py-3 text-right font-bold">
                          {formatCurrency(payrollData.reduce((sum, p) => sum + p.gross_pay, 0))}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-red-600">
                          {formatCurrency(payrollData.reduce((sum, p) => sum + p.total_deductions, 0))}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-green-600">
                          {formatCurrency(payrollData.reduce((sum, p) => sum + p.net_pay, 0))}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  給与データがありません。先に給与計算を実行してください。
                </div>
              )}
            </div>
          )}

          {/* 給与設定タブ */}
          {activeTab === 'settings' && (
            <div className="p-6">
              <div className="grid gap-4">
                {staffSettings.map((staff) => (
                  <div key={staff.staff_id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-lg">{staff.display_name}</h3>
                        <div className="mt-2 text-sm text-gray-600">
                          <span className="mr-4">
                            給与形態: {staff.pay_type === 'hourly' ? '時給' : staff.pay_type === 'daily' ? '日給' : '月給'}
                          </span>
                          <span className="mr-4">基本給: {formatCurrency(staff.base_rate)}</span>
                          <span>通勤手当: {formatCurrency(staff.transport_allowance)}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setEditingStaff({
                            ...staff,
                            pay_type: staff.pay_type || 'hourly',
                            base_rate: staff.base_rate || 1000,
                            transport_allowance: staff.transport_allowance || 0,
                            other_allowance: staff.other_allowance || 0
                          });
                          setShowSettingsModal(true);
                        }}
                        className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        編集
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 会計連携タブ */}
          {activeTab === 'export' && (
            <div className="p-6">
              <div className="border-2 border-blue-300 bg-blue-50 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-semibold text-blue-900 mb-2">会計ソフト連携（推奨）</h3>
                <p className="text-sm text-blue-800 mb-4">
                  まずはこちらの2つを使用してください（青色申告 / 給料王）
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={handleExportCSV}
                    className="w-full px-5 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-semibold"
                  >
                    <Download className="h-5 w-5" />
                    みんなの青色申告にインポート（推奨）
                  </button>
                  <button
                    onClick={handleKyuryoohExport}
                    className="w-full px-5 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 font-semibold"
                  >
                    <Download className="h-5 w-5" />
                    給料王にインポート（推奨）
                  </button>
                </div>
              </div>

              <h3 className="text-base font-semibold text-gray-700 mb-4">旧/非推奨（詳細操作）</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* みんなの青色申告エクスポート */}
                <div className="border rounded-lg p-6">
                  <div className="text-center">
                    <FileText className="h-16 w-16 mx-auto text-blue-500 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">
                      みんなの青色申告
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      給与仕訳データを会計ソフト用のCSVファイルとして出力します
                    </p>
                    <button
                      onClick={handleExportCSV}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
                    >
                      <Download className="h-4 w-4" />
                      仕訳CSV出力（旧）
                    </button>
                  </div>
                </div>

                {/* 給料王エクスポート */}
                <div className="border rounded-lg p-6">
                  <div className="text-center">
                    <Calculator className="h-16 w-16 mx-auto text-green-500 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">
                      給料王
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      勤怠データを給料王のインポート形式（レイアウト１）で出力します
                    </p>
                    <div className="space-y-2">
                      <button
                        onClick={fetchKyuryoohPreview}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2 mx-auto"
                      >
                        <FileText className="h-4 w-4" />
                        プレビュー
                      </button>
                      <button
                        onClick={handleKyuryoohExport}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 mx-auto"
                      >
                        <Download className="h-4 w-4" />
                        勤怠CSV出力（旧）
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* 給料王プレビューモーダル */}
              {showKyuryoohPreview && kyuryoohPreview && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                  <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-bold">給料王エクスポートプレビュー</h3>
                      <button
                        onClick={() => setShowKyuryoohPreview(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        ✕
                      </button>
                    </div>

                    {/* サマリー表示 */}
                    <div className="mb-6">
                      <h4 className="font-semibold mb-3">勤怠集計サマリー</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-gray-300">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="border border-gray-300 px-4 py-2 text-left">社員コード</th>
                              <th className="border border-gray-300 px-4 py-2 text-left">氏名</th>
                              <th className="border border-gray-300 px-4 py-2 text-center">出勤日数</th>
                              <th className="border border-gray-300 px-4 py-2 text-center">総労働時間</th>
                              <th className="border border-gray-300 px-4 py-2 text-center">残業時間</th>
                              <th className="border border-gray-300 px-4 py-2 text-center">深夜労働時間</th>
                            </tr>
                          </thead>
                          <tbody>
                            {kyuryoohPreview.summary?.map((staff: any, index: number) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="border border-gray-300 px-4 py-2">{staff.employeeCode}</td>
                                <td className="border border-gray-300 px-4 py-2">{staff.name}</td>
                                <td className="border border-gray-300 px-4 py-2 text-center">{staff.workDays}日</td>
                                <td className="border border-gray-300 px-4 py-2 text-center">{staff.formattedTotal}</td>
                                <td className="border border-gray-300 px-4 py-2 text-center">{staff.formattedOvertime}</td>
                                <td className="border border-gray-300 px-4 py-2 text-center">{staff.formattedNight}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* CSVプレビュー */}
                    <div className="mb-6">
                      <h4 className="font-semibold mb-3">CSVデータプレビュー（{kyuryoohPreview.layout}）</h4>
                      <div className="bg-gray-100 p-4 rounded overflow-x-auto">
                        <pre className="text-xs">
                          {kyuryoohPreview.csvRecords?.map((record: any[]) =>
                            record.join(',')
                          ).join('\n')}
                        </pre>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => setShowKyuryoohPreview(false)}
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                      >
                        閉じる
                      </button>
                      <button
                        onClick={handleKyuryoohExport}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        CSVダウンロード
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* スタッフ設定編集モーダル */}
        {showSettingsModal && editingStaff && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">
                  {editingStaff.display_name}の給与設定
                </h3>
                <button
                  onClick={() => {
                    setShowSettingsModal(false);
                    setEditingStaff(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    給与形態
                  </label>
                  <select
                    value={editingStaff.pay_type || 'hourly'}
                    onChange={(e) => setEditingStaff({
                      ...editingStaff,
                      pay_type: e.target.value as 'hourly' | 'daily' | 'monthly'
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="hourly">時給</option>
                    <option value="daily">日給</option>
                    <option value="monthly">月給</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    基本給（{editingStaff.pay_type === 'hourly' ? '時給' : editingStaff.pay_type === 'daily' ? '日給' : '月給'}）
                  </label>
                  <input
                    type="number"
                    value={editingStaff.base_rate || 0}
                    onChange={(e) => setEditingStaff({
                      ...editingStaff,
                      base_rate: Number(e.target.value)
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    通勤手当（月額）
                  </label>
                  <input
                    type="number"
                    value={editingStaff.transport_allowance || 0}
                    onChange={(e) => setEditingStaff({
                      ...editingStaff,
                      transport_allowance: Number(e.target.value)
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    各種手当（月額）
                  </label>
                  <input
                    type="number"
                    value={editingStaff.other_allowance || 0}
                    onChange={(e) => setEditingStaff({
                      ...editingStaff,
                      other_allowance: Number(e.target.value)
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingStaff.is_owner || false}
                    onChange={(e) => setEditingStaff({
                      ...editingStaff,
                      is_owner: e.target.checked
                    })}
                    className="h-4 w-4 text-blue-600 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">
                    オーナー（時給ルール適用外）
                  </label>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowSettingsModal(false);
                    setEditingStaff(null);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                >
                  キャンセル
                </button>
                <button
                  onClick={async () => {
                    try {
                      console.log("💾 給与設定保存開始:", editingStaff);
                      const response = await apiClient.put(
                        `/payroll/settings/${editingStaff.staff_id}`,
                        {
                          pay_type: editingStaff.pay_type,
                          base_rate: editingStaff.base_rate,
                          transport_allowance: editingStaff.transport_allowance,
                          other_allowance: editingStaff.other_allowance,
                          is_owner: editingStaff.is_owner
                        }
                      );
                      console.log("✅ 給与設定保存成功:", response.data);

                      setMessage({
                        type: 'success',
                        text: '給与設定を更新しました'
                      });

                      // スタッフ設定を再取得
                      await fetchStaffSettings();

                      setShowSettingsModal(false);
                      setEditingStaff(null);
                    } catch (error) {
                      console.error('給与設定更新エラー:', error);
                      setMessage({
                        type: 'error',
                        text: '給与設定の更新に失敗しました'
                      });
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  保存
                </button>
              </div>
            </div>
          </div>
        )}
  
      {/* 勤怠編集モーダル */}
      <AttendanceEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        record={editingRecord}
        staffList={editModalStaffList}
        onSave={handleSaveRecord}
        mode={modalMode}
      />
    </PageContainer>
  );
};

export default PayrollManagementEnhanced;
